import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ManageAccounts as ManageIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';

// Shared input styles for consistency
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    fontFamily: 'Metropolis, sans-serif',
    '& fieldset': {
      borderColor: '#E5E5E5',
    },
    '&:hover fieldset': {
      borderColor: '#D9DBDC',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4698D3',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': {
      color: '#4698D3',
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#999999',
  },
};

// Reserved realm names that cannot be used for customers
const RESERVED_REALM_NAMES = ['jit', 'docs', 'scim', 'login', 'cli', 'apps', 'master', 'default', 'aws-sso', 'admin'];

// Shared dialog styles
const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E5E5',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
};

const CustomerManagement = () => {
  const navigate = useNavigate();
  const { customers, loading, createCustomer, fetchCustomers, updateCustomer, deleteCustomer, getCustomerResourceCounts } = useApi();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [resourceCounts, setResourceCounts] = useState(null);
  const [loadingResourceCounts, setLoadingResourceCounts] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    description: '',
    subdomain: '',
    enableOrgServices: false,
    enableSelfServeOnboarding: false,
    managementAccountId: '',
    sharedPayer: false,
  });
  const [subdomainError, setSubdomainError] = useState('');
  const [tenantOUStatus, setTenantOUStatus] = useState(null);
  const [verifyingTenantOU, setVerifyingTenantOU] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm({
      ...customerForm,
      [name]: value,
    });

    // Validate subdomain for reserved names and hyphen rules
    if (name === 'subdomain') {
      const normalizedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (RESERVED_REALM_NAMES.includes(normalizedValue)) {
        setSubdomainError(`'${normalizedValue}' is a reserved name and cannot be used`);
      } else if (normalizedValue.startsWith('-') || normalizedValue.endsWith('-')) {
        setSubdomainError('Subdomain cannot start or end with a hyphen');
      } else if (normalizedValue.includes('--')) {
        setSubdomainError('Subdomain cannot contain consecutive hyphens');
      } else {
        setSubdomainError('');
      }
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      await createCustomer({
        ...customerForm,
        realmName: customerForm.subdomain.toLowerCase().replace(/[^a-z0-9-]/g, ''),
      });
      toast.success(`Customer "${customerForm.name}" created successfully`);
      setShowCreateForm(false);
      setCustomerForm({ name: '', description: '', subdomain: '' });
      setSubdomainError('');
    } catch (err) {
      toast.error(err.message || 'Failed to create customer');
      console.error('Failed to create customer:', err);
    }
  };

  const handleEditCustomer = (customer) => {
    setCustomerToEdit(customer);
    setCustomerForm({
      name: customer.name,
      description: customer.description || '',
      subdomain: customer.realmName,
      enableOrgServices: customer.enableOrgServices || false,
      enableSelfServeOnboarding: customer.enableSelfServeOnboarding || false,
      managementAccountId: customer.managementAccountId || '',
      sharedPayer: customer.sharedPayer || false,
    });
    setTenantOUStatus(null);
    setShowEditForm(true);
  };

  // sanitizeName must mirror prism-ops orgs.SanitizeName: lowercase, non-alnum
  // collapse to '-', trim, max 64 chars. Used to preview the AWS OU name ops
  // must create manually for shared-payer customers.
  const sanitizeName = (name) => {
    if (!name) return '';
    let out = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    out = out.replace(/-+/g, '-').replace(/^-|-$/g, '');
    if (out.length > 64) out = out.slice(0, 64).replace(/-+$/, '');
    return out;
  };

  const handleVerifyTenantOU = async () => {
    if (!customerToEdit) return;
    setVerifyingTenantOU(true);
    setTenantOUStatus(null);
    try {
      // Pass current dialog state so admin can verify a soon-to-be-saved
      // configuration without persisting first.
      const resp = await apiService.verifyTenantOU(customerToEdit.id, {
        customerName: customerForm.name,
        mgmt: customerForm.managementAccountId,
        sharedPayer: customerForm.sharedPayer,
      });
      setTenantOUStatus(resp);
      if (resp?.found) {
        toast.success(`Tenant OU verified (${resp.account_count} account${resp.account_count === 1 ? '' : 's'})`);
      } else {
        toast.warning('Tenant OU not found in AWS — please create it manually');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to verify tenant OU');
    } finally {
      setVerifyingTenantOU(false);
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if ((window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas' &&
        (customerForm.enableOrgServices || customerForm.enableSelfServeOnboarding) &&
        !customerForm.managementAccountId?.trim()) {
      const which = customerForm.enableOrgServices && customerForm.enableSelfServeOnboarding
        ? 'Org Services and Self-Serve Onboarding'
        : customerForm.enableOrgServices
          ? 'Org Services'
          : 'Self-Serve Onboarding';
      toast.error(`Management Account ID is required to enable ${which}`);
      return;
    }
    try {
      await updateCustomer(customerToEdit.id, {
        ...customerForm,
        realmName: customerForm.subdomain.toLowerCase().replace(/[^a-z0-9-]/g, ''),
        enableOrgServices: customerForm.enableOrgServices,
        enableSelfServeOnboarding: customerForm.enableSelfServeOnboarding,
        managementAccountId: customerForm.managementAccountId?.trim() || undefined,
        sharedPayer: customerForm.sharedPayer,
      });
      toast.success(`Customer "${customerForm.name}" updated successfully`);
      setShowEditForm(false);
      setCustomerToEdit(null);
      setCustomerForm({ name: '', description: '', subdomain: '', enableOrgServices: false, enableSelfServeOnboarding: false, managementAccountId: '', sharedPayer: false });
      setTenantOUStatus(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update customer');
      console.error('Failed to update customer:', err);
    }
  };

  const handleManageCustomer = (customer) => {
    navigate(`/customers/${customer.id}/dashboard`);
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteDialog(true);
    loadResourceCounts(customer.id);
  };

  const loadResourceCounts = async (customerId) => {
    setLoadingResourceCounts(true);
    try {
      const counts = await getCustomerResourceCounts(customerId);
      setResourceCounts(counts);
    } catch (err) {
      console.error('Failed to load resource counts:', err);
      setResourceCounts(null);
    } finally {
      setLoadingResourceCounts(false);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      const result = await deleteCustomer(customerToDelete.id);
      if (result) {
        toast.success(`Customer "${customerToDelete.name}" deleted successfully`);
        setShowDeleteDialog(false);
        setCustomerToDelete(null);
        setResourceCounts(null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete customer');
      console.error('Failed to delete customer:', err);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setCustomerToDelete(null);
    setResourceCounts(null);
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Avatar
                sx={{
                  backgroundColor: 'rgba(70, 152, 211, 0.1)',
                  width: 48,
                  height: 48,
                }}
              >
                <BusinessIcon sx={{ fontSize: 24, color: '#4698D3' }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    color: '#253E66',
                  }}
                >
                  Customer Management
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    mt: 0.5,
                  }}
                >
                  {customers.length} customer{customers.length !== 1 ? 's' : ''} registered
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
              sx={{
                backgroundColor: '#4698D3',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1,
                boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                '&:hover': {
                  backgroundColor: '#3580B8',
                  boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                },
              }}
            >
              Add Customer
            </Button>
          </Box>
        </Paper>
      </motion.div>

      {/* Customer List */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
              <CircularProgress size={40} sx={{ color: '#4698D3' }} />
              <Typography sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.95rem' }}>
                Loading customers...
              </Typography>
            </Box>
          ) : customers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
              <BusinessIcon sx={{ fontSize: 64, color: '#E5E5E5', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#253E66', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, mb: 1 }}>
                No Customers Found
              </Typography>
              <Typography variant="body1" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', mb: 3 }}>
                Click "Add Customer" to create your first customer organization
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
                sx={{
                  backgroundColor: '#4698D3',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#3580B8' },
                }}
              >
                Add Your First Customer
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5' }}>
                      Customer
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5', width: 150 }}>
                      Realm
                    </TableCell>
                    {(window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas' && (
                      <>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5', width: 120 }}>
                          Org Services
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5', width: 120 }}>
                          Self-Serve
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', borderBottom: '1px solid #E5E5E5', width: 160 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: '#F9FBFD' },
                        '&:last-child td': { borderBottom: 'none' },
                      }}
                    >
                      <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ backgroundColor: 'rgba(70, 152, 211, 0.1)', width: 36, height: 36 }}>
                            <BusinessIcon sx={{ color: '#4698D3', fontSize: 18 }} />
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#253E66' }}>
                              {customer.name}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999' }}>
                              {customer.description || 'No description'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                        <Chip
                          label={customer.realmName}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(70, 152, 211, 0.1)',
                            color: '#4698D3',
                            border: '1px solid rgba(70, 152, 211, 0.2)',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      {(window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas' && (
                        <>
                          <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={customer.enableOrgServices ? 'Enabled' : 'Disabled'}
                                size="small"
                                sx={{
                                  backgroundColor: customer.enableOrgServices ? '#E8F5E9' : '#F5F5F5',
                                  color: customer.enableOrgServices ? '#2E7D32' : '#999999',
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                              {customer.managementAccountId && (
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#999' }}>
                                  {customer.managementAccountId}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                            <Chip
                              label={customer.enableSelfServeOnboarding ? 'Enabled' : 'Disabled'}
                              size="small"
                              sx={{
                                backgroundColor: customer.enableSelfServeOnboarding ? '#E8F5E9' : '#F5F5F5',
                                color: customer.enableSelfServeOnboarding ? '#2E7D32' : '#999999',
                                fontFamily: 'Metropolis, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleManageCustomer(customer)}
                            sx={{
                              color: '#4698D3',
                              backgroundColor: '#F9FBFD',
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': { backgroundColor: 'rgba(70, 152, 211, 0.1)', borderColor: '#4698D3' },
                            }}
                          >
                            <ManageIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Edit"
                            onClick={() => handleEditCustomer(customer)}
                            sx={{
                              color: '#666666',
                              backgroundColor: '#F9FBFD',
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': { backgroundColor: 'rgba(70, 152, 211, 0.1)', borderColor: '#4698D3', color: '#4698D3' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCustomer(customer)}
                            sx={{
                              color: '#666666',
                              backgroundColor: '#F9FBFD',
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.1)', borderColor: '#E74C3C', color: '#E74C3C' },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </motion.div>

      {/* Create Customer Dialog */}
      <Dialog
        open={showCreateForm}
        onClose={() => { setShowCreateForm(false); setSubdomainError(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          color: '#253E66',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2,
        }}>
          <Avatar sx={{ backgroundColor: 'rgba(70, 152, 211, 0.1)', width: 36, height: 36 }}>
            <BusinessIcon sx={{ color: '#4698D3', fontSize: 20 }} />
          </Avatar>
          Create New Customer
          <IconButton
            onClick={() => { setShowCreateForm(false); setSubdomainError(''); }}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: '#666666',
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#F0F4F8',
                color: '#253E66',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box component="form" onSubmit={handleCreateCustomer} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              name="name"
              label="Customer Name"
              value={customerForm.name}
              onChange={handleInputChange}
              required
              fullWidth
              placeholder="e.g., Acme Corporation"
              sx={inputStyles}
            />

            <TextField
              name="subdomain"
              label="Realm Name / Subdomain"
              value={customerForm.subdomain}
              onChange={handleInputChange}
              required
              fullWidth
              placeholder="e.g., acme"
              error={!!subdomainError}
              helperText={subdomainError || "Used for subdomain (e.g., 'acme' for acme.cloudkeeper.com). Only lowercase letters, numbers, and hyphens allowed."}
              sx={inputStyles}
            />

            <TextField
              name="description"
              label="Description"
              value={customerForm.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
              placeholder="Brief description of the customer organization"
              sx={inputStyles}
            />
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={() => { setShowCreateForm(false); setSubdomainError(''); }}
            disabled={loading}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#D9DBDC',
                backgroundColor: '#F9FBFD',
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomer}
            disabled={loading || !customerForm.name || !customerForm.subdomain || !!subdomainError}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
              '&:hover': {
                backgroundColor: '#3580B8',
                boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
              },
              '&:disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        open={showEditForm}
        onClose={() => setShowEditForm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          color: '#253E66',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2,
        }}>
          <Avatar sx={{ backgroundColor: 'rgba(70, 152, 211, 0.1)', width: 36, height: 36 }}>
            <EditIcon sx={{ color: '#4698D3', fontSize: 20 }} />
          </Avatar>
          Edit Customer Details
          <IconButton
            onClick={() => setShowEditForm(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: '#666666',
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#F0F4F8',
                color: '#253E66',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box component="form" onSubmit={handleUpdateCustomer} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              name="name"
              label="Customer Name"
              value={customerForm.name}
              onChange={handleInputChange}
              required
              fullWidth
              sx={inputStyles}
            />

            <TextField
              name="subdomain"
              label="Realm Name / Subdomain"
              value={customerForm.subdomain}
              onChange={handleInputChange}
              required
              fullWidth
              disabled
              helperText="Realm name cannot be changed after creation as it affects Keycloak realm configuration"
              sx={{
                ...inputStyles,
                '& .MuiOutlinedInput-root': {
                  ...inputStyles['& .MuiOutlinedInput-root'],
                  backgroundColor: '#F9FBFD',
                },
              }}
            />

            <TextField
              name="description"
              label="Description"
              value={customerForm.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
              sx={inputStyles}
            />

            {(window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas' && (
            <>
            <Divider sx={{ my: 0.5 }} />

            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#253E66',
                  mb: 1,
                }}
              >
                Feature Toggles
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={customerForm.enableOrgServices}
                    onChange={(e) => setCustomerForm({ ...customerForm, enableOrgServices: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4698D3' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4698D3' },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>
                      Org Services
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>
                      AWS Organizations structure and SCP policy management
                    </Typography>
                  </Box>
                }
                sx={{ ml: 0, alignItems: 'flex-start' }}
              />
              {(customerForm.enableOrgServices || customerForm.enableSelfServeOnboarding) && (
                <TextField
                  label="Management Account ID"
                  placeholder="123456789012"
                  value={customerForm.managementAccountId}
                  onChange={(e) => setCustomerForm({ ...customerForm, managementAccountId: e.target.value })}
                  size="small"
                  sx={{ ml: 4, mt: 1, width: 240 }}
                  inputProps={{ maxLength: 12 }}
                  helperText="12-digit AWS management account ID (required)"
                  error={(customerForm.enableOrgServices || customerForm.enableSelfServeOnboarding) && !customerForm.managementAccountId?.trim()}
                />
              )}
              {customerForm.enableOrgServices && (
                <Box sx={{ ml: 4, mt: 2, p: 2, border: '1px solid #E5E5E5', borderRadius: '6px', backgroundColor: '#F9FBFD' }}>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#253E66', mb: 1 }}>
                    Payer Mode
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={customerForm.sharedPayer}
                        onChange={(e) => { setCustomerForm({ ...customerForm, sharedPayer: e.target.checked }); setTenantOUStatus(null); }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#4698D3' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4698D3' },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>
                          Shared Payer
                        </Typography>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>
                          ON: this customer shares its AWS payer with other Prism customers; isolation enforced via tenant OU.
                          <br />OFF: this customer owns the entire payer; no isolation needed.
                        </Typography>
                      </Box>
                    }
                    sx={{ ml: 0, alignItems: 'flex-start' }}
                  />
                  {customerForm.sharedPayer && customerForm.name && (
                    <Box sx={{ mt: 2, p: 1.5, border: '1px dashed #4698D3', borderRadius: '4px', backgroundColor: '#FFF' }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                        Expected Tenant OU
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#253E66', fontWeight: 600 }}>
                        CK-Tenant-{sanitizeName(customerForm.name)}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999', mt: 1 }}>
                        Manual setup required: in the AWS console for payer{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>{customerForm.managementAccountId || '<management account>'}</Box>,
                        create this OU under <Box component="span" sx={{ fontFamily: 'monospace' }}>CK-Customer-OU-TM</Box>{' '}
                        and move this customer's accounts into it.
                      </Typography>
                      {customerToEdit && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleVerifyTenantOU}
                            disabled={verifyingTenantOU || !customerForm.managementAccountId}
                            sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', borderColor: '#4698D3', color: '#4698D3' }}
                          >
                            {verifyingTenantOU ? 'Verifying…' : 'Verify OU exists'}
                          </Button>
                          {tenantOUStatus && (
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: tenantOUStatus.found ? '#2E7D32' : '#C0392B' }}>
                              {tenantOUStatus.found
                                ? `OU found (${tenantOUStatus.account_count} account${tenantOUStatus.account_count === 1 ? '' : 's'})`
                                : 'OU not found'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={customerForm.enableSelfServeOnboarding}
                    onChange={(e) => setCustomerForm({ ...customerForm, enableSelfServeOnboarding: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4698D3' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4698D3' },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>
                      Self-Serve Onboarding
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>
                      Allow customers to onboard accounts to CK Billing
                    </Typography>
                  </Box>
                }
                sx={{ ml: 0, mt: 1, alignItems: 'flex-start' }}
              />
            </Box>
            </>
            )}
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={() => setShowEditForm(false)}
            disabled={loading}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#D9DBDC',
                backgroundColor: '#F9FBFD',
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCustomer}
            disabled={loading || !customerForm.name || !customerForm.subdomain}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
              '&:hover': {
                backgroundColor: '#3580B8',
                boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
              },
              '&:disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Update Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Customer Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={closeDeleteDialog}
        maxWidth="md"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'rgba(231, 76, 60, 0.08)',
            borderBottom: '1px solid rgba(231, 76, 60, 0.2)',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#E74C3C',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Avatar sx={{ backgroundColor: 'rgba(231, 76, 60, 0.15)', width: 36, height: 36 }}>
            <WarningIcon sx={{ color: '#E74C3C', fontSize: 20 }} />
          </Avatar>
          Delete Customer: {customerToDelete?.name}
          <IconButton
            onClick={closeDeleteDialog}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: '#E74C3C',
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              border: '1px solid rgba(231, 76, 60, 0.2)',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'rgba(231, 76, 60, 0.15)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(231, 76, 60, 0.08)',
              border: '1px solid rgba(231, 76, 60, 0.2)',
              '& .MuiAlert-icon': { color: '#E74C3C' },
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66' }}>
              This action is <strong>irreversible</strong> and will permanently delete all customer data.
            </Typography>
          </Alert>

          {customerToDelete && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                Customer Information
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  p: 2,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Name
                    </Typography>
                    <Typography sx={{ color: '#253E66', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                      {customerToDelete.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Realm
                    </Typography>
                    <Typography sx={{ color: '#253E66', fontFamily: 'monospace', fontWeight: 500 }}>
                      {customerToDelete.realmName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      ID
                    </Typography>
                    <Typography sx={{ color: '#253E66', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {customerToDelete.id}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {loadingResourceCounts ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#4698D3', mr: 2 }} />
              <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                Loading resource counts...
              </Typography>
            </Box>
          ) : resourceCounts ? (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                Resources to be Deleted
              </Typography>
              <Grid container spacing={1.5}>
                {Object.entries(resourceCounts.resourceCounts || {}).map(([key, count]) => (
                  <Grid item xs={6} sm={4} key={key}>
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: count > 0 ? 'rgba(231, 76, 60, 0.05)' : '#F9FBFD',
                        border: `1px solid ${count > 0 ? 'rgba(231, 76, 60, 0.2)' : '#E5E5E5'}`,
                        borderRadius: '6px',
                        p: 1.5,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', textTransform: 'capitalize', fontSize: '0.7rem' }}>
                        {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      </Typography>
                      <Typography variant="h6" sx={{ color: count > 0 ? '#E74C3C' : '#253E66', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                        {count}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: 'rgba(231, 76, 60, 0.08)',
                      border: '1px solid rgba(231, 76, 60, 0.2)',
                      borderRadius: '6px',
                      p: 1.5,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#E74C3C', fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                      Total Resources: {resourceCounts.totalResources || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(70, 152, 211, 0.08)',
                border: '1px solid rgba(70, 152, 211, 0.2)',
                '& .MuiAlert-icon': { color: '#4698D3' },
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66' }}>
                Unable to load resource counts. Deletion will proceed but may affect unknown resources.
              </Typography>
            </Alert>
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#253E66',
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            This will delete:
          </Typography>

          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              p: 2,
            }}
          >
            <Box component="ul" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', pl: 2, m: 0, fontSize: '0.9rem', lineHeight: 1.8 }}>
              <li>All customer users from Keycloak realm</li>
              <li>All customer groups and memberships</li>
              <li>All permission sets and assignments</li>
              <li>All onboarded AWS accounts</li>
              <li>All AWS IAM roles created for this customer</li>
              <li>All custom applications and SAML configurations</li>
              <li>Customer admin accounts</li>
              <li>The entire Keycloak realm</li>
              <li>All database records for this customer</li>
            </Box>
          </Paper>

          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#E74C3C',
              fontWeight: 600,
              mt: 2,
              textAlign: 'center',
            }}
          >
            This action cannot be undone!
          </Typography>
          </motion.div>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5, backgroundColor: '#F9FBFD', borderTop: '1px solid #E5E5E5' }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={loading}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#D9DBDC',
                backgroundColor: '#FFFFFF',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDeleteCustomer}
            disabled={loading}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(231, 76, 60, 0.25)',
              '&:hover': {
                backgroundColor: '#C0392B',
                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.35)',
              },
              '&:disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete Customer Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

    </motion.div>
  );
};

export default CustomerManagement;
